import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { autoBeforeEachTest } from '@atlassian/jira-platform-auto-before-each-test/src/auto-before-each-test.tsx';

/**
 * This class helps detect if we are making more network requests than we expect for the mainIssueAggQuery query
 */
class AggOverfetchDetector {
	private previousIssueKeys = new Set<string>();

	private issueKey: string | undefined;

	private isDisabled = false;

	private routerResourceFetched = false;

	private relayFetches = 0;

	private ssrFetchSuccess = false;

	private hasSentAnalytics = false;

	/**
	 * This is used for when we initially load the page and don't make additional network requests - eg through ssr
	 * @param issueKey
	 * @param forceSSRFetchSuccess If provided, forces the ssrFetchSuccess value instead of checking the globalThis window variable. This is mainly used for testing
	 */
	setInitialIssueKey(issueKey: string, forceSSRFetchSuccess?: boolean) {
		if (!this.issueKey) {
			this.issueKey = issueKey;

			if (forceSSRFetchSuccess === undefined) {
				if (globalThis?.window?.SPA_STATE?.['ssr/render_status'] === 'success') {
					this.ssrFetchSuccess = Boolean(
						globalThis?.window?.SPA_STATE?.['ssr/relay_fetch']?.mainIssueAggQuery,
					);
				}
			} else {
				this.ssrFetchSuccess = forceSSRFetchSuccess;
			}
		}
	}

	private reset() {
		if (this.issueKey) {
			this.previousIssueKeys.add(this.issueKey);
		}
		this.issueKey = undefined;
		this.isDisabled = false;
		this.routerResourceFetched = false;
		this.relayFetches = 0;
		this.ssrFetchSuccess = false;
	}

	onRouterResourceFetchComplete(issueKey: string) {
		/**
		 * We can't correctly tell if we're fetching again if we're navigating to an issue we've previously seen
		 * eg, KAN-1 -> KAN-2 -> KAN-1
		 *
		 * I believe this is because the router resource will grab the data for the issue from the relay store since it already exists
		 * but we then make another fetch outside that to get the correct data
		 *
		 * We end up detecting it as multiple fetches but we only do 1 network request (from non router resource)
		 *
		 * So if we are fetching from router resource for an old issue key, the detector ignores it
		 */
		if (this.previousIssueKeys.has(issueKey)) {
			return;
		}
		if (this.issueKey && this.issueKey !== issueKey) {
			this.reset();
		}
		this.issueKey = issueKey;
		if (this.isDisabled) {
			return;
		}
		this.routerResourceFetched = true;
		this.verifyNoOverfetch();
	}

	onRelayFetchComplete(issueKey: string) {
		if (this.issueKey && this.issueKey !== issueKey) {
			this.reset();
		}
		this.issueKey = issueKey;
		if (this.isDisabled) {
			return;
		}
		this.relayFetches += 1;
		this.verifyNoOverfetch();
	}

	private verifyNoOverfetch() {
		if (this.isDisabled || this.hasSentAnalytics) {
			return;
		}
		const { routerResourceFetched, relayFetches: standardFetches, ssrFetchSuccess } = this;

		const totalFetches =
			(routerResourceFetched ? 1 : 0) + standardFetches + (ssrFetchSuccess ? 1 : 0);

		if (totalFetches > 1) {
			fireErrorAnalytics({
				error: new Error('AGG query overfetch detected'),
				meta: {
					id: 'jiraIssueAggOverfetchDetector',
					packageName: 'jiraIssueFetchOverfetchDetector',
					teamName: 'Issue View - Kenji',
				},
				attributes: {
					routerResourceFetched,
					standardFetches,
					totalFetches,
					ssrFetchSuccess,
				},
			});
			this.hasSentAnalytics = true;
		}
	}

	/**
	 * Disables the overfetch detector for a specific issue key.
	 * This is useful for when we know we will be calling mainIssueAggQuery again intentionally, eg for soft refresh after mutations
	 * @param issueKey The issue key to disable the detector for.
	 */
	disableForIssueKey(issueKey: string) {
		if (this.issueKey === issueKey) {
			this.isDisabled = true;
		}
	}
}

let aggOverfetchDetector = new AggOverfetchDetector();
autoBeforeEachTest(() => {
	aggOverfetchDetector = new AggOverfetchDetector();
});

export const getAggOverfetchDetector = () => aggOverfetchDetector;
